/**
 * RichTextEditor — lightweight WYSIWYG based on Tiptap.
 *
 * Provides bold, italic, underline, headings and text-align controls.
 * Stores content as HTML string.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { useEffect, useRef, useState } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  /** Minimal height in px */
  minHeight?: number;
  /**
   * If provided, the toolbar shows an "Insert image" button. The callback
   * receives the picked File and must return the public URL to embed once
   * the upload finishes. Errors should throw — the editor will toast and
   * leave the document unchanged.
   */
  onImageUpload?: (file: File) => Promise<string>;
}

const HEADING_LEVELS = [1, 2, 3] as const;

export default function RichTextEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 120,
  onImageUpload,
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // Images get a tailored CSS class so the post styles them consistently
      // with the rest of the editor content (max-width, rounded corners…).
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: { class: 'blog-inline-image rounded-lg my-4 max-w-full h-auto' },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate({ editor: e }) {
      onChange(e.getHTML());
    },
  });

  const handleImagePick = () => {
    if (!editor || !onImageUpload || uploading) return;
    fileInputRef.current?.click();
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-picked later
    e.target.value = '';
    if (!file || !editor || !onImageUpload) return;

    setUploading(true);
    try {
      const url = await onImageUpload(file);
      if (url) {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      }
    } catch (err) {
      // Surface to consumer through console; the consumer's onImageUpload
      // is expected to toast for user-facing messages.
      // eslint-disable-next-line no-console
      console.error('[RichTextEditor] image upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  // Sync editable flag when disabled changes
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Sync content from outside (e.g., when switching blocks)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // Only run when value prop changes, not on every editor update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btnBase =
    'px-2 py-1 rounded text-xs font-medium transition-colors border border-transparent';
  const btnActive = 'bg-vida-100 text-vida-700 border-vida-300';
  const btnInactive = 'text-gray-600 hover:bg-gray-100';

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
          {/* Bold / Italic / Underline */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`${btnBase} ${editor.isActive('bold') ? btnActive : btnInactive} font-bold`}
            title="Negrita"
          >
            N
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`${btnBase} ${editor.isActive('italic') ? btnActive : btnInactive} italic`}
            title="Cursiva"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`${btnBase} ${editor.isActive('underline') ? btnActive : btnInactive} underline`}
            title="Subrayado"
          >
            S
          </button>

          <span className="w-px h-5 bg-gray-300 mx-1" />

          {/* Headings */}
          {HEADING_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
              className={`${btnBase} ${editor.isActive('heading', { level }) ? btnActive : btnInactive}`}
              title={`Encabezado ${level}`}
            >
              H{level}
            </button>
          ))}

          <button
            type="button"
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={`${btnBase} ${editor.isActive('paragraph') ? btnActive : btnInactive}`}
            title="Texto normal"
          >
            P
          </button>

          <span className="w-px h-5 bg-gray-300 mx-1" />

          {/* Alignment */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`${btnBase} ${editor.isActive({ textAlign: 'left' }) ? btnActive : btnInactive}`}
            title="Alinear izquierda"
          >
            &#8676;
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`${btnBase} ${editor.isActive({ textAlign: 'center' }) ? btnActive : btnInactive}`}
            title="Centrar"
          >
            &#8596;
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`${btnBase} ${editor.isActive({ textAlign: 'right' }) ? btnActive : btnInactive}`}
            title="Alinear derecha"
          >
            &#8677;
          </button>

          <span className="w-px h-5 bg-gray-300 mx-1" />

          {/* Lists */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : btnInactive}`}
            title="Lista con viñetas"
          >
            &bull; Lista
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : btnInactive}`}
            title="Lista numerada"
          >
            1. Lista
          </button>

          {onImageUpload && (
            <>
              <span className="w-px h-5 bg-gray-300 mx-1" />
              <button
                type="button"
                onClick={handleImagePick}
                disabled={uploading}
                className={`${btnBase} ${btnInactive} ${uploading ? 'opacity-50 cursor-wait' : ''}`}
                title="Insertar imagen"
              >
                {uploading ? 'Subiendo…' : '🖼️ Imagen'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Hidden file input for image insertion */}
      {onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageFile}
          className="hidden"
        />
      )}

      {/* Editor area */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-2 focus:outline-none"
        style={{ minHeight }}
      />
    </div>
  );
}
